import React, { Component, Fragment } from 'react';
import cx from 'classnames';
import { compose, Query } from '../reactApolloCompat';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Hidden from './Hidden';
import Tab from '@mui/material/Tab';
import Tabs from './layout/Tabs';
import DataForm, { DataFormSaveMode } from './DataForm';
import Spinner from './loading/Spinner';
import { buildTabFragmentLookup, createFragments } from '../utils/fragment';
import Separator from './layout/Separator';
import { BackIcon, NextIcon } from './IconIndex';
import Stepper from './stepper/Stepper';

/**
 * Wraps a data form for viewing. Requires a few parameters to work properly
 *
 * @param {array} tabs - an array of tab objects. The tab has the following structure:
 * {
 *     id: "SomeTab1"                              unique identifier that maps back to this tab
 *     label: "My Tab"                             the visible label of this tab
 *     component: SomeTab1View                     a component to render the form fields
 *     fragment: SomeFragment                      a fragment to load the form data
 *     validation: SomeTab1ValidationObject        validation fields & functions for this tab only
 *     onLoad: func(rawData)                       clean newly-loaded data before using (e.g. remove edges/nodes)
 *     formatSaveData: func(send, all, original)   manipulate data to send before saving
 *     disabled: false                             allows to disable specific tab/step
 * }
 * @param {array} additionalActions - adds additional set of buttons to DataForm with custom actions. Contains objects with structure:
 * {
 *     label: "My Button"        visible label of button
 *     onClick: func(e)          callback function for action
 *     disabled: false           is button disabled
 *     skipSave: false           function unclear ;) look in to DataForm->renderAllButtons() for more details
 * }
 * @param {bool} createNew - whether a new object should be created when this view is shown
 * @param {function} createNewFunc - creates an empty version of the object
 * @param {object} client - Apollo Client to be used by DataForm
 * @param {string} objectType - type of the dataObject in the database
 * @param {string} parentObjectType - type of the parent/connected dataObject in the database
 * @param {string} parentId - id of the parent dataObject to connect to when saving
 * @param {string} name - identifier for this DataForm, for tracking bugs etc
 * @param {object} context - parent view/component
 * @param {object|boolean} parentDataForm - if nested, pass in "true" to avoid history.unblock collisions, or pass in the parent form if it needs to be refreshed/revalidated
 * @param {string} itemId - id of the dataObject to load
 * @param {function(form): void} onDataChanged - executes when data in form was changed, also applies continuous validation
 * @param {boolean} continuousValidation - enables behaviour where validation happens during user input and starts when form loaded. Default - false, form will be validated on save
 * @param {boolean} useValidation - decide if HTML Constraint Validation (non-javascript) is also used. NOTE: Not seems to work good with stepped view, locking save button on next step, not on current
 * @param {object} validation - global validation objects that apply to all tabs (unless tab has its own validation)
 * @param {function(data): void} onCreated - executes after a new object has been saved to the database
 * @param {function(data): void} onUpdated - executes after an existing object has been saved to the database
 * @param {function(form): boolean} onBeforeSave - executes before DataForm saved, will receive current instance of DataForm and should return boolean to approve or cancel saving
 * @param {function(event, int): void} onPageChange - executes when selectedIndex was changed. Arguments: (<event|null>, <selectedIndex>). Event provided only if view in Tabbed mode
 * @param {function(array): void} onStepComplete - executes when view in stepper mode and step was completed. Arguments: (<completedStep>,<allCompletedSteps>)
 * @param {function(data): void} onLoad - fires each time form finishes loading new data
 * @param {0|1|2} viewMode - use the import DataFormViewMode to select single, tabbed or stepped view
 * @param {int} selectedIndex - value to be passed in to the state to choose selected tab from outside
 * @param {function(int): void} viewLoaded function that is called once a view loads for the first time. NOTE: There's a bit of kink here. It doesn't trigger on the first view (because the viewLoaded is called in componentDidUpdate). Gotta fix this eventually
 * @param {boolean} readOnly - stops save button being rendered
 * @param {boolean} useFixedStepperBehaviour - fixes stepper mode behaviour, by default fix is disabled to preserve behaviour for code that could rely on broken behaviour
 * @param {object} tabProps - passed as property 'tabProps' of component of currently active tab|step
 * @param {0|1|2} saveMode - Available modes: currentTab - saves changes that are made on currently opened tab/step, currentFragment - saves changes for all tabs/pages that use same fragment as current tab, all - save changes to all tabs/pages.
 * @param {boolean} keepComponentWhileLoading - render component while loading in progress, child components need to be ready to deal with loading and empty data
 * @param {boolean} saveNotValid - enable saving the form even if required fields failed validation
 * @param {function(confirmCallback,cancelCallback): void} historyBlockHandler - function to handle user prompt for leaving page with unsaved changes. If user confirms leaving - confirmCallback() should be called, and if not - cancelCallback(). Use confirmCallback(true) to force navigation without saving
 */

export const DataFormViewMode = {
    single: 0,
    tabbed: 1,
    stepped: 2
};

class DataFormView extends Component {
    state = {
        selectedIndex: null,
        itemId: null,
        createNew: false,
        tabFragmentLookup: null,
        fragments: null,
        viewMode: DataFormViewMode.single,
        completedSteps: new Set(),
        skippedSteps: null,
        loaded: [],
        readQuery: null,
        saveMode: null //making sure that we know that we did not have selected some saveMode already
    };

    constructor(props) {
        super(props);
        this.htmlForm = React.createRef();
    }

    static getDerivedStateFromProps({ itemId, createNew, tabs, viewMode, selectedIndex, saveMode }, oldState) {
        let newState = { ...oldState };

        //build the tab lookup if it hasn't been built yet
        if (oldState.tabFragmentLookup === null) {
            newState.tabFragmentLookup = buildTabFragmentLookup(tabs, true);
            newState.fragments = createFragments(tabs);
            newState.loaded = tabs.map(x => false);

            if (!viewMode) newState.viewMode = tabs.length > 1 ? DataFormViewMode.tabbed : DataFormViewMode.single;
            else newState.viewMode = viewMode;
        }

        if (oldState.selectedIndex === null) {
            newState.selectedIndex = selectedIndex !== undefined ? selectedIndex : 0;
        }

        if (newState.saveMode === null) {
            newState.saveMode = saveMode === undefined ? DataFormSaveMode.currentTab : saveMode;
        } else if (newState.saveMode !== saveMode && saveMode !== undefined) {
            throw new Error('Once set, save mode can not be changed');
        }

        if (
            viewMode === DataFormViewMode.stepped &&
            (oldState.completedSteps == null || oldState.skippedSteps == null)
        ) {
            newState.completedSteps = new Set();
            newState.skippedSteps = new Set();
        }
        if (createNew) {
            if (oldState.itemId === null) {
                //if its brand new, set it to createNew = true
                newState.createNew = true;
            } else {
                //this was a createNew, but it's been saved and now has a itemId
                newState.createNew = false;
            }
        } else if (itemId !== oldState.itemId) {
            //this is an existing thing
            newState.itemId = itemId;
        }
        return newState;
    }

    componentDidUpdate() {
        const { loaded, selectedIndex } = this.state;

        if (!loaded[selectedIndex]) {
            loaded[selectedIndex] = true;
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({ loaded });

            const { viewLoaded } = this.props;
            if (viewLoaded) {
                viewLoaded(selectedIndex);
            }
        }
    }

    render() {
        const { selectedIndex, itemId, createNew } = this.state;
        const { tabs, createNewFunc, objectType, client } = this.props;
        const tab = tabs[selectedIndex];

        if (createNew) {
            return this.renderForm({
                loading: false,
                data: { [`readOne${objectType}`]: createNewFunc() }
            });
        }

        const query = gql`
            ${tab.fragment}
            query readOne${objectType}($id: ID!) {
                readOne${objectType}(ID: $id) {
                    ID
                    ...${tab.fragment.definitions[0].name.value}
                }
            }
        `;

        return (
            <Query
               query={query}
               variables={{ id: itemId }}
               client={client}
               fetchPolicy="cache-and-network"
            >
                {results => this.renderForm(results, query)}
            </Query>
        );
    }

    renderForm({ loading, error, data }, query) {
        const {
            parentId,
            parentDataForm,
            tabs,
            name,
            parentObjectType,
            objectType,
            context,
            onUpdated,
            onLoad,
            onBeforeSave,
            classes,
            buttonLabels,
            useValidation,
            validation,
            readOnly,
            className,
            additionalActions,
            children,
            client,
            keepComponentWhileLoading,
            saveNotValid,
            historyBlockHandler
        } = this.props;
        const { selectedIndex, itemId, tabFragmentLookup, fragments, viewMode, createNew, saveMode } = this.state;
        const tab = tabs[selectedIndex];

        if (loading && (!data || !data[0]) && !keepComponentWhileLoading) return this.renderLoading();
        if (error) return this.renderError(error);

        //get the save functions
        let saveFunc = createNew ? s => this.onCreateNew(s) : s => onUpdated && onUpdated(s);

        //get the correct button labels
        let steppedButtonLabels = null;
        if (viewMode === DataFormViewMode.stepped) {
            const { completedSteps } = this.state;

            steppedButtonLabels = {
                isUnsaved: completedSteps.size === tabs.length ? 'Submit' : 'Save',
                isSaved: completedSteps.size === tabs.length ? 'Submit' : 'Save'
            };
        }

        //get the appropriate function to render the view
        let renderViewFunc = null;
        switch (viewMode) {
            case DataFormViewMode.single:
                renderViewFunc = this.renderSingleView;
                break;
            case DataFormViewMode.tabbed:
                renderViewFunc = this.renderTabbedView;
                break;
            case DataFormViewMode.stepped:
                renderViewFunc = this.renderSteppedView;
                break;
            default:
                throw new Error('unknown view mode', name);
        }

        return (
            <div className={className}>
                <DataForm
                    name={name}
                    id={createNew ? parentId : itemId}
                    idType={createNew ? `${parentObjectType}ID` : 'ID'}
                    tab={tab}
                    loading={loading}
                    error={error}
                    onSaved={saveFunc}
                    onLoad={onLoad}
                    onBeforeSave={onBeforeSave}
                    data={data && data[`readOne${objectType}`]}
                    mutationName={createNew ? `create${objectType}` : `update${objectType}`}
                    mutationInputType={createNew ? `Create${objectType}Input` : `Update${objectType}Input`}
                    createNew={createNew}
                    context={context}
                    fragments={fragments}
                    tabs={tabs}
                    tabFragmentLookup={tabFragmentLookup}
                    parentDataForm={parentDataForm}
                    buttonLabels={buttonLabels || steppedButtonLabels}
                    selectedIndex={selectedIndex}
                    readOnly={readOnly}
                    validate={useValidation === true ? () => this.validate() : null}
                    validation={validation}
                    additionalActions={additionalActions}
                    customClient={client}
                    saveMode={saveMode}
                    onChange={this.onFormDataChanged}
                    saveNotValid={saveNotValid}
                    historyBlockHandler={historyBlockHandler}
                    query={query}
                >
                    {(form, saveButton) => (
                        <Fragment>
                            {!!loading && (
                                <div
                                    style={{
                                        position: 'absolute',
                                        zIndex: 99,
                                        margin: -20,
                                        padding: 20,
                                        height: '100%',
                                        width: '100%',
                                        background: 'rgba(255,255,255,0.5)'
                                    }}
                                >
                                    <span>Loading data, please wait a moment...</span>
                                    <div style={{ float: 'left', marginRight: 8 }}>
                                        <Spinner />
                                    </div>
                                </div>
                            )}
                            {readOnly ? (
                                renderViewFunc(form, saveButton, query) //Read-only DataFormViews don't require form element.
                            ) : (
                                <form autoComplete="off" className={classes.form} ref={this.htmlForm}>
                                    {renderViewFunc(form, saveButton, query)}
                                </form>
                            )}
                            {children}
                        </Fragment>
                    )}
                </DataForm>
            </div>
        );
    }

    renderSingleView = (form, saveButton, query) => {
        const { classes, tabs, createNew, itemId, parentId, tabProps, context } = this.props;
        const { selectedIndex } = this.state;
        const tab = tabs[selectedIndex];
        const validationBadges = () => this.renderTabLabel(null, tab.id);
        return (
            <Fragment>
                <tab.component
                    form={form}
                    createNew={createNew}
                    itemId={itemId}
                    parentId={parentId}
                    saveButton={saveButton}
                    tabProps={tabProps}
                    context={context}
                    query={query}
                    validationBadges={validationBadges}
                />

                <div className={`${classes.footerButtons} formFooter`}>
                    {this.renderButtons(saveButton, tab.buttons)}
                </div>
            </Fragment>
        );
    };

    renderSteppedView = (form, saveButton, query) => {
        const { classes, tabs, className } = this.props;
        const { selectedIndex, completedSteps } = this.state;
        const tab = tabs[selectedIndex];
        return (
            <span className={className}>
                <Grid item xs={12} className={classes.stepperContainer}>
                    <div>
                        {tabs.length > 1 && (
                            <Stepper
                                alternativeLabel
                                nonLinear
                                activeStep={selectedIndex}
                                tabs={tabs}
                                completedSteps={completedSteps}
                                onStepperStep={index => this.onStepperStep(index)}
                            />
                        )}
                        <div>
                            {this.renderTabContent(selectedIndex, form, null, query)}
                            {saveButton}
                            <div
                                className={
                                    selectedIndex === 0
                                        ? cx(classes.buttonHolder, classes.firstPageButtons)
                                        : selectedIndex === tabs.length - 1
                                            ? cx(classes.buttonHolder, classes.lastPageButtons)
                                            : classes.buttonHolder
                                }
                            >
                                {selectedIndex > 0 && (
                                    <Button
                                        disabled={selectedIndex === 0}
                                        variant="contained"
                                        color="primary"
                                        onClick={e => this.onStepperBack()}
                                    >
                                        <BackIcon />
                                        <Hidden xsDown>Previous Page</Hidden>
                                    </Button>
                                )}
                                {selectedIndex < tabs.length - 1 &&
                                    (tab.isOptional === true ? (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={e => this.onStepperSkip()}
                                            className={classes.button}
                                        >
                                            Skip
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={this.onStepperNext}
                                            className={classes.button}
                                        >
                                            <Hidden xsDown>Next Page</Hidden>
                                            <NextIcon />
                                        </Button>
                                    ))}
                            </div>
                        </div>
                    </div>
                </Grid>
            </span>
        );
    };

    renderTabbedView = (form, saveButton, query) => {
        const { classes, tabs, className } = this.props;
        const { selectedIndex } = this.state;

        return (
            <Fragment>
                <span className={className}>
                    <div className={classes.root}>
                        <Tabs value={selectedIndex} onChange={this.onChange} variant="scrollable" scrollButtons="on">
                            {tabs.map((tab, index) => (
                                <Tab
                                     key={tab.id || index}
                                     selected={index === selectedIndex}
                                     label={this.renderTabLabel((tab.title || tab.label), tab.id)}
                                     disabled={tab.disabled}
                                />
                            ))}
                        </Tabs>
                        <Separator />
                        <div className={classes.content}>
                            {this.renderSingleView(form, saveButton, query)}
                        </div>
                    </div>
                </span>
            </Fragment>
        );
    };

    renderTabLabel(tabLabel, tabId) {
        return (
            <div className="tab-label">
                {tabLabel && <div>{tabLabel}</div>}
                <div className="badgeContainer">
                    <div title="Please fix required fields" className="badgeError" tab-error={tabId} />
                    <div title="Please check suggested fields" className="badgeSuggested" tab-suggested={tabId} />
                    <div title="Fields do not match data from Family" className="badgeOptional" tab-optional={tabId} />
                </div>
            </div>
        );
    }

    renderButtons(saveButton, otherButtons) {
        if (Array.isArray(otherButtons))
            return (
                <Fragment>
                    {otherButtons}
                    {saveButton}
                </Fragment>
            );
        if (otherButtons instanceof Function) return otherButtons(saveButton);
        return saveButton;
    }

    renderError(error) {
        const { classes, className } = this.props;
        console.error('an error occurred loading the sum tabs', error);
        return (
            <span className={className}>
                <div className={classes.graphQLFeedbackFull}>An error occurred</div>
            </span>
        );
    }

    renderLoading() {
        const { classes, className } = this.props;
        return (
            <span className={className}>
                <div className={classes.root}>
                    <div className={classes.loading}>
                        <Spinner />
                    </div>
                </div>
            </span>
        );
    }

    onChange = (event, selectedIndex) => {
        this.setState({ selectedIndex });
        const { onPageChange } = this.props;
        if (onPageChange) onPageChange(event, selectedIndex);
    };

    onCreateNew(newObject) {
        this.setState({ itemId: newObject.ID, createNew: false });
        const { onCreated } = this.props;
        if (onCreated) onCreated(newObject);
    }

    onStepperStep(index) {
        const { onPageChange, tabs, useFixedStepperBehaviour } = this.props;

        this.updateValidation();
        //Preventing from stepping in to the disabled step
        if (!tabs[index].disabled || !useFixedStepperBehaviour) {
            this.setState({ selectedIndex: index });
            if (onPageChange) onPageChange(null, index);
        }
    }

    onStepperNext = () => {
        let { selectedIndex, completedSteps } = this.state;
        const { tabs } = this.props;

        //=======this code is the part of the fix for stepper mode============
        const { useFixedStepperBehaviour } = this.props;
        //====================================================================
        if (useFixedStepperBehaviour) {
            this.updateValidation();
        }
        let nextIndex = selectedIndex + 1;
        if (nextIndex === tabs.length) {
            //=======this code is the part of the fix for stepper mode============
            if (!useFixedStepperBehaviour) {
                //====================================================================
                if (completedSteps.size === 0) {
                    //cycle to start
                    nextIndex = 0;
                } else if (completedSteps.size !== tabs.length - 1) {
                    //cycle to next incomplete step
                    const indices = tabs.map((x, i) => i);
                    nextIndex = indices.find(x => !completedSteps.has(x));
                }
                //=======this code is the part of the fix for stepper mode============
            } else {
                const indices = tabs.map((x, i) => i);
                nextIndex = indices.find(x => !completedSteps.has(x));
            }
            //====================================================================
        }

        if (tabs[nextIndex].disabled) {
            nextIndex = selectedIndex; //next tab locked - staying here
        }

        if (this.props.onPageChange) this.props.onPageChange(null, nextIndex);
        this.setState({ selectedIndex: nextIndex, completedSteps });

        if (!useFixedStepperBehaviour && !completedSteps.has(selectedIndex)) {
            completedSteps.add(selectedIndex);
        }

        //=======this code is the part of the fix for stepper mode============
        if (!useFixedStepperBehaviour) {
            //====================================================================
            if (completedSteps.size === tabs.length - 1) this.onStepperComplete();
            //=======this code is the part of the fix for stepper mode============
        }
        //====================================================================
    };

    onStepperBack = () => {
        let { selectedIndex } = this.state;
        if (selectedIndex === 0) return;

        selectedIndex--;
        this.setState({ selectedIndex });
        if (this.props.onPageChange) this.props.onPageChange(null, selectedIndex);
    };

    onStepperSkip = () => {
        let { selectedIndex, skippedSteps } = this.state;
        const { tabs } = this.props;
        const tab = tabs[selectedIndex];

        if (tab.isOptional !== false) return;

        skippedSteps.add(selectedIndex);

        if (selectedIndex !== tabs.length - 1) selectedIndex++;

        this.setState({ skippedSteps, selectedIndex });
        if (this.props.onPageChange) this.props.onPageChange(null, selectedIndex);
    };

    onStepperComplete = () => {
        const { onStepperComplete } = this.props;
        if (onStepperComplete) onStepperComplete();
    };

    validate() {
        if (!this.htmlForm.current) return true;
        return this.htmlForm.current.checkValidity();
    }

    updateValidation() {
        const { onStepComplete, tabs } = this.props;
        const completedSteps = new Set();
        if (this.form && tabs) {
            this.form.validateForm();
            const results = this.form.validation.tabs;
            tabs.forEach((tab, index) => {
                if (!results[tab.id] || results[tab.id].invalid === 0) {
                    completedSteps.add(index);
                }
            });
        }
        const { completedSteps: stateCompletedSteps } = this.state;
        if (stateCompletedSteps.size !== completedSteps.size && onStepComplete) {
            onStepComplete(completedSteps);
        }
        this.setState({ completedSteps });
    }

    onFormDataChanged = form => {
        this.form = form;
        const { continuousValidation, onDataChanged } = this.props;
        if (continuousValidation) this.updateValidation();
        if (onDataChanged) onDataChanged(form);
    };
}

const styles = () => ({
    footerButtons: {
        paddingTop: '5px',
        display: 'flex',
        justifyContent: 'flex-end'
    }
});

DataFormView.propTypes = {
    objectType: PropTypes.string.isRequired
};

export default compose(withStyles(styles))(DataFormView);
